import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../../firebase/config';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import './SubcategoryManager.css';

const getInitialForm = () => ({
  label: '',
  description: '',
  order: 0,
  categoryId: '',
});

function SubcategoryManager({ compact = false, onChange }) {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [formData, setFormData] = useState(getInitialForm());
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const categoryMap = useMemo(() => {
    return categories.reduce((acc, category) => {
      acc[category.id] = category.label || category.id;
      return acc;
    }, {});
  }, [categories]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const categoriesRef = collection(db, 'categories');
      const categoriesSnap = await getDocs(query(categoriesRef, orderBy('order', 'asc')));
      const categoriesData = categoriesSnap.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setCategories(categoriesData);

      const subcategoriesRef = collection(db, 'subcategories');
      const subcategoriesSnap = await getDocs(query(subcategoriesRef, orderBy('order', 'asc')));
      const subcategoriesData = subcategoriesSnap.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setSubcategories(subcategoriesData);
      if (onChange) {
        onChange(subcategoriesData);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setCategories([]);
      setSubcategories([]);
      if (onChange) {
        onChange([]);
      }
      setFeedback({ type: 'error', message: 'Failed to load subcategories' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'order' ? parseInt(value, 10) || 0 : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.label.trim()) {
      setFeedback({ type: 'error', message: 'Subcategory name is required' });
      return;
    }

    if (!formData.categoryId) {
      setFeedback({ type: 'error', message: 'Please select a parent category' });
      return;
    }

    setIsSaving(true);
    setFeedback(null);

    try {
      const subcategoryData = {
        label: formData.label.trim(),
        description: formData.description.trim() || '',
        order: formData.order || 0,
        categoryId: formData.categoryId,
        updatedAt: serverTimestamp(),
      };

      if (editingSubcategory) {
        const subcategoryRef = doc(db, 'subcategories', editingSubcategory.id);
        await updateDoc(subcategoryRef, {
          ...subcategoryData,
          createdAt: editingSubcategory.createdAt,
        });
        setFeedback({ type: 'success', message: 'Subcategory updated successfully' });
      } else {
        await addDoc(collection(db, 'subcategories'), {
          ...subcategoryData,
          createdAt: serverTimestamp(),
        });
        setFeedback({ type: 'success', message: 'Subcategory added successfully' });
      }

      setFormData(getInitialForm());
      setEditingSubcategory(null);
      await loadData();
      setTimeout(() => setFeedback(null), 3000);
    } catch (error) {
      console.error('Error saving subcategory:', error);
      setFeedback({ type: 'error', message: 'Failed to save subcategory' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (subcategory) => {
    setEditingSubcategory(subcategory);
    setFormData({
      label: subcategory.label || '',
      description: subcategory.description || '',
      order: subcategory.order || 0,
      categoryId: subcategory.categoryId || '',
    });
    document.querySelector('.subcategory-form-container')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCancel = () => {
    setEditingSubcategory(null);
    setFormData(getInitialForm());
    setFeedback(null);
  };

  const handleDelete = async (subcategoryId) => {
    setIsSaving(true);
    try {
      await deleteDoc(doc(db, 'subcategories', subcategoryId));
      setFeedback({ type: 'success', message: 'Subcategory deleted successfully' });
      setShowDeleteConfirm(null);
      await loadData();
      setTimeout(() => setFeedback(null), 3000);
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      setFeedback({ type: 'error', message: 'Failed to delete subcategory' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className={`subcategory-manager ${compact ? 'compact' : ''}`}>
        <div className="loading-state">Loading subcategories...</div>
      </div>
    );
  }

  return (
    <div className={`subcategory-manager ${compact ? 'compact' : ''}`}>
      <div className="subcategory-header">
        <h2>Subcategory Manager</h2>
        <p>Create and organize subcategories under your main categories.</p>
      </div>

      {feedback && (
        <div className={`feedback-message ${feedback.type}`}>
          {feedback.message}
        </div>
      )}

      <div className="subcategory-form-container">
        <h3>{editingSubcategory ? 'Edit Subcategory' : 'Add New Subcategory'}</h3>
        <form onSubmit={handleSubmit} className="subcategory-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="label">Subcategory Name *</label>
              <input
                type="text"
                id="label"
                name="label"
                value={formData.label}
                onChange={handleInputChange}
                placeholder="e.g., Evening Abayas"
                required
                disabled={isSaving}
              />
              <small>This will be displayed to customers</small>
            </div>
            <div className="form-group">
              <label htmlFor="order">Display Order</label>
              <input
                type="number"
                id="order"
                name="order"
                value={formData.order}
                onChange={handleInputChange}
                min="0"
                disabled={isSaving}
              />
              <small>Lower numbers appear first</small>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="categoryId">Parent Category *</label>
            <select
              id="categoryId"
              name="categoryId"
              value={formData.categoryId}
              onChange={handleInputChange}
              required
              disabled={isSaving}
            >
              <option value="">Select a category</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.label || category.id}
                </option>
              ))}
            </select>
            {categories.length === 0 && (
              <small style={{ color: '#dc2626' }}>Add a category first.</small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="description">Description (Optional)</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Brief description of this subcategory"
              rows="3"
              disabled={isSaving}
            />
          </div>

          <div className="form-actions">
            {editingSubcategory && (
              <button
                type="button"
                onClick={handleCancel}
                className="btn-cancel"
                disabled={isSaving}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="btn-save"
              disabled={isSaving || !formData.label.trim() || !formData.categoryId}
            >
              {isSaving ? 'Saving...' : editingSubcategory ? 'Update Subcategory' : 'Add Subcategory'}
            </button>
          </div>
        </form>
      </div>

      <div className="subcategories-list-container">
        <h3>Existing Subcategories ({subcategories.length})</h3>
        {subcategories.length === 0 ? (
          <div className="empty-state">
            <p>No subcategories yet. Add your first subcategory above.</p>
          </div>
        ) : (
          <div className="subcategories-grid">
            {subcategories.map((subcategory) => (
              <div key={subcategory.id} className="subcategory-card">
                <div className="subcategory-card-header">
                  <div className="subcategory-info">
                    <h4>{subcategory.label}</h4>
                    {subcategory.description && (
                      <p className="subcategory-description">{subcategory.description}</p>
                    )}
                    <div className="subcategory-meta">
                      <span className="subcategory-category">
                        Category: {categoryMap[subcategory.categoryId] || 'Unassigned'}
                      </span>
                      <span className="subcategory-order">Order: {subcategory.order || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="subcategory-card-actions">
                  <button
                    onClick={() => handleEdit(subcategory)}
                    className="btn-edit"
                    disabled={isSaving}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(subcategory.id)}
                    className="btn-delete"
                    disabled={isSaving}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Subcategory?</h3>
            <p>
              Are you sure you want to delete this subcategory? This action cannot be undone.
              Products using this subcategory will need to be updated manually.
            </p>
            <div className="modal-actions">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="btn-delete-confirm"
                disabled={isSaving}
              >
                {isSaving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubcategoryManager;
